import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

import { isApiError, queryKeys } from '@/shared/api';

const MAX_RETRIES = 2;

/**
 * Retry only what can succeed on retry: network failures and 5xx.
 * 4xx are deterministic (validation, permissions, not found).
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (isApiError(error) && error.status >= 400 && error.status < 500) return false;
  return failureCount < MAX_RETRIES;
}

export function createQueryClient(): QueryClient {
  // Session expired/revoked, or the CSRF token is stale: re-check the viewer.
  // If the session is gone, RequireAuth then redirects to /login.
  const onError = (error: unknown) => {
    if (
      isApiError(error) &&
      (error.code === 'AUTHENTICATION_REQUIRED' || error.code === 'CSRF_FAILED')
    ) {
      void client.invalidateQueries({ queryKey: queryKeys.viewer });
    }
  };
  const client: QueryClient = new QueryClient({
    queryCache: new QueryCache({ onError }),
    mutationCache: new MutationCache({ onError }),
    defaultOptions: {
      // Cached boards/cards stay usable for 15 min (shown at once, refreshed in the background).
      // The whole cache is dropped on sign-out, so nothing crosses a user boundary.
      queries: { staleTime: 30_000, gcTime: 15 * 60_000, retry: shouldRetry },
      mutations: { retry: false },
    },
  });
  return client;
}
